import {NgxPermissionsService} from 'ngx-permissions';
import {PageTitleService} from './shared/services/page-title.service';
import {Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {Platform} from '@angular/cdk/platform';
import {FuseSplashScreenService} from '../@fuse/services/splash-screen.service';
import {FuseSidebarService} from '../@fuse/components/sidebar/sidebar.service';
import {FuseNavigationService} from '../@fuse/components/navigation/navigation.service';
import {DOCUMENT} from '@angular/common';
import {FuseConfigService} from '../@fuse/services/config.service';
import {map, switchMap, take, takeUntil} from 'rxjs/operators';
import {fuseNavigation} from './fuse-navigation';
import {FuseTranslationLoaderService} from '../@fuse/services/translation-loader.service';
import {TranslateService} from '@ngx-translate/core';
import {locale as navigationEnglish} from 'app/locale/navigation/en';
import {MsalService} from '@azure/msal-angular';
import {AmchartsHelper} from './shared/amcharts.helper';
import {FuseConfig, FuseNavigation} from '../@fuse/types';
import {Event, NavigationEnd, NavigationStart, ResolveEnd, Router} from '@angular/router';
import {TorchBadgeUpdatedListener, TorchNavigationService} from './shared/services/torch-navigation.service';
import {Angulartics2Mixpanel} from 'angulartics2/mixpanel';
import {TorchMaintenanceService} from '../@backend/modules';
import {TorchChangelogService} from '@backend/modules/@documentation/services/torch-changelog.service';
import {TorchFeedbackService} from '@backend/modules/@users/services/torch-feedback.service';
import {SessionService} from './shared/services/session.service';
import {fuseAnimations} from '../@fuse/animations';
import {GlobalCacheConfig, LocalStorageStrategy} from 'ts-cacheable';
import {SoftwareUpdateService} from './shared/services/software-update.service';
import {LoggingService} from './shared/services/logging.service';

GlobalCacheConfig.storageStrategy = LocalStorageStrategy;
GlobalCacheConfig.maxAge = 60000; // 60 seconds
GlobalCacheConfig.slidingExpiration = true;
GlobalCacheConfig.maxCacheCount = 100;

const Favico = require('favico.js');

AmchartsHelper.useTheme('ANIMATED');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: fuseAnimations
})
export class AppComponent implements OnInit, OnDestroy, TorchBadgeUpdatedListener {
  fuseConfig: FuseConfig;
  navigation: FuseNavigation[];

  isChangingThemes: Observable<boolean>;

  // Private
  private readonly unsubscribeAll: Subject<any>;
  private readonly favicon;
  private lastURL: string;


  /**
   * Constructor
   *
   * @param document
   * @param fuseConfigService
   * @param fuseNavigationService
   * @param fuseSidebarService
   * @param fuseSplashScreenService
   * @param fuseTranslationLoaderService
   * @param translateService
   * @param platform
   * @param authService: MsalService
   * @param router: Router
   * @param pageTitleService: PageTitleService
   * @param torchNavigationService: TorchNavigationService
   * @param zone
   * @param permissionsService
   * @param angulartics2mixpanel
   * @param torchMaintenanceService
   * @param torchChangelogService
   * @param torchFeedbackService
   * @param sessionService
   * @param softwareUpdateService
   * @param loggingService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private fuseConfigService: FuseConfigService,
    private fuseNavigationService: FuseNavigationService,
    private fuseSidebarService: FuseSidebarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translateService: TranslateService,
    private platform: Platform,
    private authService: MsalService,
    private router: Router,
    private pageTitleService: PageTitleService,
    private torchNavigationService: TorchNavigationService,
    private zone: NgZone,
    private permissionsService: NgxPermissionsService,
    private angulartics2mixpanel: Angulartics2Mixpanel,
    private torchMaintenanceService: TorchMaintenanceService,
    private torchChangelogService: TorchChangelogService,
    private torchFeedbackService: TorchFeedbackService,
    private sessionService: SessionService,
    private softwareUpdateService: SoftwareUpdateService,
    private loggingService: LoggingService
  ) {

    this.isChangingThemes = this.sessionService.isChangingThemes;
    angulartics2mixpanel.startTracking();

    this.favicon = new Favico({
      animation: 'slide'
    });

    this.permissionsService.getPermissions();

    // Get default navigation
    this.navigation = fuseNavigation;

    // Register the navigation to the service
    this.fuseNavigationService.register('main', this.navigation);

    // Set the main navigation as our current navigation
    this.fuseNavigationService.setCurrentNavigation('main');

    // Add languages
    this.translateService.addLangs(['en', 'tr']);

    // Set the default language
    this.translateService.setDefaultLang('en');

    // Set the navigation translations
    this.fuseTranslationLoaderService.loadTranslations(navigationEnglish);

    // Use a language
    this.translateService.use('en');

    // Add is-mobile class to the body if the platform is mobile
    if (this.platform.ANDROID || this.platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    // Set the private defaults
    this.unsubscribeAll = new Subject();

    // Add listener to handle navigation badge updates
    this.torchNavigationService.addBadgeUpdatedListener(this);
    this.torchChangelogService.addBadgeUpdatedListener(this);
    this.torchFeedbackService.addBadgeUpdatedListener(this);


    this.torchMaintenanceService.listDisabledModules().subscribe(response => {
      const disabledPages = response.data;

      disabledPages.forEach(disabledPage => {
        const rawNavigationItem: FuseNavigation = this.torchMaintenanceService.navigationItems
          .find(navItem => navItem.title === disabledPage.module);

        if (!!rawNavigationItem) {
          const navigationItem: FuseNavigation = this.fuseNavigationService.getNavigationItem(rawNavigationItem.id);
          navigationItem.classes = 'disabled-maintenance';

          this.fuseNavigationService.updateNavigationItem(navigationItem.id, navigationItem);
        }
      });
    });

    router.events.pipe(takeUntil(this.unsubscribeAll))
      .subscribe((event: Event) => {
        if (event instanceof NavigationStart) {
          this.pageTitleService.reset();
        } else if (event instanceof ResolveEnd || event instanceof NavigationEnd ) {
          if (!!this.lastURL && event.url.split('#')[0] !== this.lastURL) {
            this.updateTheme(this.fuseConfig).pipe(
              take(1)
            ).subscribe((config) => {
              this.fuseConfig = config;
              this.loggingService.log('Theme updated successfully:', config.colorTheme);
            });
          }

          this.lastURL = event.url.split('#')[0];
        }
      });

    this.torchChangelogService.getLatestVersion().subscribe(logItem => {
      this.badgeUpdated('changelog', logItem.version, false);
    });

    this.torchFeedbackService.getCount().subscribe(count => {
      this.badgeUpdated('user-feedback', count, false);
    });


  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to config changes
    this.fuseConfigService.config.pipe(
      takeUntil(this.unsubscribeAll),
      switchMap(config => this.updateTheme(config))
    ).subscribe((config) => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      this.document.body.classList.forEach(className => {
        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      });

      this.document.body.classList.add(this.fuseConfig.colorTheme);
      this.torchNavigationService.setupListeners();
    });

    this.permissionsService.permissions$.subscribe((permissions) => {
      if (permissions.hasOwnProperty('torchGlobalAdmin')) {
        this.toggleAdminNavigation(true);
        this.toggleOtherNavigation('mod-overview', true);
        this.toggleOtherNavigation('eo-budget-forecast', true);
        this.toggleOtherNavigation('internal-crt-metrics', true);
        // this.toggleOtherNavigation('executive-kpi-dashboard', true);
      } else {
        this.toggleAdminNavigation();
        this.toggleOtherNavigation('mod-overview', false);
        this.toggleOtherNavigation('eo-budget-forecast', false);
        this.toggleOtherNavigation('internal-crt-metrics', false);
        // this.toggleOtherNavigation('executive-kpi-dashboard', false);
      }

      if (permissions.hasOwnProperty('torchGlobalAdmin') || permissions.hasOwnProperty('materielAdmin')) {
        this.toggleOtherNavigation('shelf-life-expiration', true);
        this.toggleOtherNavigation('lobo-report', true);
      } else {
        this.toggleOtherNavigation('shelf-life-expiration', false);
        this.toggleOtherNavigation('lobo-report', false);
      }

      if (permissions.hasOwnProperty('torchGlobalAdmin') || permissions.hasOwnProperty('fleetTeamAdmin')) {
        this.toggleOtherNavigation('fleet-team', true);
      } else {
        this.toggleOtherNavigation('fleet-team', false);
      }

      if (permissions.hasOwnProperty('crtInternalMetricsUser')) {
        this.toggleOtherNavigation('internal-crt-metrics', true);
      } else {
        this.toggleOtherNavigation('internal-crt-metrics', false);
      }

      if (permissions.hasOwnProperty('eoBudgetAdmin')) {
        this.toggleOtherNavigation('eo-budget-forecast', true);
      } else {
        this.toggleOtherNavigation('eo-budget-forecast', false);
      }

      if (permissions.hasOwnProperty('leadershipInsightsMember')) {
        this.toggleOtherNavigation('leadership-insights', true);
      } else {
        this.toggleOtherNavigation('leadership-insights', false);
      }

      // if (permissions.hasOwnProperty('techOpsKpiUser')) {
      //   this.toggleOtherNavigation('techops-kpi-pbi', true);
      //   this.toggleOtherNavigation('executive-kpi-dashboard', true);
      //   this.toggleOtherNavigation('techops-kpi-collapsable', true);
      // } else {
      //   this.toggleOtherNavigation('techops-kpi-pbi', false);
      //   this.toggleOtherNavigation('executive-kpi-dashboard', false);
      //   this.toggleOtherNavigation('techops-kpi-collapsable', false);
      // }

      // if (permissions.hasOwnProperty('torchGlobalAdmin') || permissions.hasOwnProperty('weightBalanceAdmin')) {
      //   this.toggleOtherNavigation('wb-flight-query', true);
      //   this.toggleOtherNavigation('devops-engineering', true);
      //   this.toggleOtherNavigation('fleet-statistics-by-fleet', true);
      //   this.toggleOtherNavigation('fleet-statistics-a300', true);
      //   this.toggleOtherNavigation('fleet-statistics-b757', true);
      //   this.toggleOtherNavigation('fleet-statistics-b767', true);
      //   this.toggleOtherNavigation('fleet-statistics-b777', true);
      //   this.toggleOtherNavigation('fleet-statistics-md11', true);
      //   this.toggleOtherNavigation('fleet-statistics-other', true);
      //   this.toggleOtherNavigation('fleet-statistics-trunk', true);
      //   this.toggleOtherNavigation('fleet-statistics-feeder', true);
      //   this.toggleOtherNavigation('fleet-statistics-custom', true);
      // } else {
      //   this.toggleOtherNavigation('wb-flight-query', false);
      //   this.toggleOtherNavigation('devops-engineering', false);
      //   this.toggleOtherNavigation('fleet-statistics-by-fleet', false);
      //   this.toggleOtherNavigation('fleet-statistics-a300', false);
      //   this.toggleOtherNavigation('fleet-statistics-b757', false);
      //   this.toggleOtherNavigation('fleet-statistics-b767', false);
      //   this.toggleOtherNavigation('fleet-statistics-b777', false);
      //   this.toggleOtherNavigation('fleet-statistics-md11', false);
      //   this.toggleOtherNavigation('fleet-statistics-other', false);
      //   this.toggleOtherNavigation('fleet-statistics-trunk', false);
      //   this.toggleOtherNavigation('fleet-statistics-feeder', false);
      //   this.toggleOtherNavigation('fleet-statistics-custom', false);
      // }
    });

    // @ts-ignore
    window.hasLoaded = true;
    console.log('Updated hasLoaded on window');
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  logout() {
    this.authService.logout();
  }

  toggleAdminNavigation(show: boolean = false) {
    const navigationItem = this.fuseNavigationService.getNavigationItem('admin');
    navigationItem.hidden = !show;
    this.fuseNavigationService.updateNavigationItem('admin', navigationItem);
  }

  toggleOtherNavigation(name: string, show: boolean = false) {
    const navigationItem = this.fuseNavigationService.getNavigationItem(name);
    navigationItem.hidden = !show;
    this.fuseNavigationService.updateNavigationItem(name, navigationItem);
  }

  badgeUpdated(id: string, value: number | string, favicon = true) {
    const navigationItem = this.fuseNavigationService.getNavigationItem(id);
    let badgeForegroundColor = '#FFF';
    let badgeBackgroundColor = '#FF1493';

    if (favicon === true) {
      this.favicon.badge(value);
    }

    if (navigationItem.hasOwnProperty('badge')) {
      if (navigationItem.badge.hasOwnProperty('fg')) {
        badgeForegroundColor = navigationItem.badge.fg;
      }

      if (navigationItem.badge.hasOwnProperty('bg')) {
        badgeBackgroundColor = navigationItem.badge.bg;
      }

      delete navigationItem.badge;
    }

    this.zone.run(() => {
      this.fuseNavigationService.updateNavigationItem(id, navigationItem);
      setTimeout(() => {
        navigationItem.badge = {
          title: value,
          fg: badgeForegroundColor,
          bg: badgeBackgroundColor
        };
        this.fuseNavigationService.updateNavigationItem(id, navigationItem);
      }, 0);
    });
  }

  private updateTheme(config) {
    return this.sessionService.darkMode.pipe(
      map(theme => {
        if (!!theme && config.colorTheme !== theme) {
          config.colorTheme = theme;
        }
        return config;
      })
    );
  }
}
